import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Progress } from '../ui/progress';
import { toast } from 'sonner';
import FeelingsNeedsHelper from '../FeelingsNeedsHelper';
import FloatingFeelingsHelper from '../shared/FloatingFeelingsHelper';
import {
  FeelIcon, NeedIcon, GoldIcon,
  SaveIcon, ChapterIcon, ArrowRightIcon, ArrowLeftIcon, InsightIcon,
  ExpandIcon, CollapseIcon, ResetIcon, DownloadIcon, SparkleIcon,
  ArrowDownIcon, ObserveIcon, PartnerIcon
} from '../icons/ElchmeIcons';

// ElchmE Branding Component - Multicolor letters
const ElchmEBrand = ({ size = 'lg', showSubtitle = true }) => {
  const sizeClasses = {
    sm: 'text-lg',
    md: 'text-2xl',
    lg: 'text-3xl',
    xl: 'text-4xl'
  };
  
  return (
    <a 
      href="https://elchme.com" 
      target="_blank" 
      rel="noopener noreferrer"
      className="group inline-block"
      data-testid="elchme-brand-link"
    >
      <span className={`font-light tracking-wide ${sizeClasses[size]}`}>
        <span className="text-blue-400 group-hover:text-blue-300 transition-colors">El</span>
        <span className="text-rose-400 group-hover:text-rose-300 transition-colors">ch</span>
        <span className="text-emerald-400 group-hover:text-emerald-300 transition-colors">m</span>
        <span className="text-amber-400 group-hover:text-amber-300 transition-colors">E</span>
      </span>
      {showSubtitle && (
        <span className="block text-xs text-slate-400 group-hover:text-slate-300 transition-colors">
          Empathetic Alchemy Platform
        </span>
      )}
    </a>
  );
};

// ElchmE Callout Card Component
const ElchmECallout = () => (
  <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 border-2 border-purple-500/30 rounded-2xl p-6 relative overflow-hidden">
    {/* Animated background elements */}
    <div className="absolute top-0 left-0 w-24 h-24 bg-blue-500/10 rounded-full blur-2xl animate-pulse" />
    <div className="absolute bottom-0 right-0 w-32 h-32 bg-amber-500/10 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '1s' }} />
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 bg-emerald-500/5 rounded-full blur-3xl" />
    
    <div className="relative z-10">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <SparkleIcon size={28} color="#A855F7" />
            <span className="text-purple-300 text-sm font-medium">EXPANDED VERSION</span>
          </div>
          <ElchmEBrand size="lg" />
          <p className="text-slate-300 mt-3 max-w-md">
            Take this Communication Wheel experience deeper with our full <span className="text-purple-300 font-semibold">Empathetic Alchemy</span> platform — 
            interactive tools, guided sessions, and AI-assisted NVC practice.
          </p>
        </div>
        
        <a 
          href="https://elchme.com"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white px-6 py-3 rounded-xl font-semibold transition-all hover:scale-105 shadow-lg shadow-purple-500/20"
          data-testid="elchme-cta-button"
        >
          <span>Explore ElchmE.com</span>
          <ArrowRightIcon size={20} />
        </a>
      </div>
      
      {/* Features row */}
      <div className="flex flex-wrap gap-3 mt-4 pt-4 border-t border-slate-700/50">
        <span className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-xs">Interactive Wheel</span>
        <span className="px-3 py-1 bg-rose-500/20 text-rose-300 rounded-full text-xs">Feelings Library</span>
        <span className="px-3 py-1 bg-emerald-500/20 text-emerald-300 rounded-full text-xs">Needs Discovery</span>
        <span className="px-3 py-1 bg-amber-500/20 text-amber-300 rounded-full text-xs">Strategy Building</span>
        <span className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-xs">AI-Assisted</span>
      </div>
    </div>
  </div>
);

// Wisdom Pearls
const wisdomPearls = [
  {
    id: 'conflicts',
    icon: '⚡',
    title: 'Conflicts Are Between Strategies',
    content: 'Our conflicts are always between strategies. They are never between our needs. When we argue, we\'re fighting over HOW to meet needs, not the needs themselves.',
    color: 'amber'
  },
  {
    id: 'green',
    icon: '🌱',
    title: 'Needs Are The Green',
    content: 'Needs are the Green within us — what we need to grow, the core elemental requirements of life at its fullest potential. This often involves belonging.',
    color: 'emerald'
  },
  {
    id: 'superpower',
    icon: '✨',
    title: 'Your Superpower',
    content: 'Focus on your needs AND their needs with radical empathy, understanding, and compassion — solutions will always find us! This is our superpower. We cannot resist when this occurs.',
    color: 'purple'
  },
  {
    id: 'weeds',
    icon: '🎯',
    title: 'Drop Into Needs',
    content: '"Drop into the needs, it will get you out of the weeds." Do not get caught up in strategy! It\'s not wrong, it\'s just less effective. Stay at the needs level.',
    color: 'teal'
  },
  {
    id: 'gifts',
    icon: '🎁',
    title: 'Triggers Are Gifts',
    content: 'These "triggers" are gifts — signaling the need for self-empathy. There is something we are not fully contemplating or addressing at the needs level.',
    color: 'rose'
  },
  {
    id: 'tenderness',
    icon: '💗',
    title: 'Radical Tenderness',
    content: 'Proceed with radical acceptance of yourself and the other. Stay in a place of extreme tenderness, no matter how righteous or non-righteous anyone is.',
    color: 'pink'
  },
  {
    id: 'belonging',
    icon: '🤝',
    title: 'Belonging Is Key',
    content: 'So much of our conflict has to do with belonging. Stop making people wrong. Just ask: "Is this meeting our needs or not?"',
    color: 'blue'
  },
  {
    id: 'connect',
    icon: '💚',
    title: 'Always Connect to Needs',
    content: 'Always, always, always connect to needs and do not make them wrong — especially when in conflict over strategies.',
    color: 'green'
  }
];

export default function CompassionateWheelEnhanced() {
  const navigate = useNavigate();
  const [isPresentationMode, setIsPresentationMode] = useState(false);
  const [currentSection, setCurrentSection] = useState(0);
  const [isHelperOpen, setIsHelperOpen] = useState(false);
  const [expandedPearl, setExpandedPearl] = useState(null);
  const [activeQuadrant, setActiveQuadrant] = useState(null);

  // Wheel data - 4 quadrants for different perspectives
  const [wheelData, setWheelData] = useState(() => {
    const saved = localStorage.getItem('compassionateWheelData');
    return saved ? JSON.parse(saved) : {
      self: {
        needs: ['', '', ''],
        feelingsMet: '',
        feelingsUnmet: '',
        strategies: ''
      },
      other: {
        needs: ['', '', ''],
        feelingsMet: '',
        feelingsUnmet: '',
        strategies: ''
      },
      situation: '',
      insights: '',
      mutualStrategy: ''
    };
  });

  const sections = [
    { id: 'intro', title: 'The Wheel', page: 25 },
    { id: 'wisdom', title: 'Wisdom Pearls', page: 25 },
    { id: 'wheel', title: 'Interactive Wheel', page: 25 },
    { id: 'integration', title: 'Integration', page: 25 },
  ];

  const progress_pct = ((currentSection + 1) / sections.length) * 100;

  // Save progress
  const saveProgress = () => {
    localStorage.setItem('compassionateWheelData', JSON.stringify(wheelData));
    toast.success('Progress saved locally!');
  };

  const resetAll = () => {
    if (window.confirm('Reset all wheel data? This cannot be undone.')) {
      setWheelData({
        self: { needs: ['', '', ''], feelingsMet: '', feelingsUnmet: '', strategies: '' },
        other: { needs: ['', '', ''], feelingsMet: '', feelingsUnmet: '', strategies: '' },
        situation: '', insights: '', mutualStrategy: ''
      });
      localStorage.removeItem('compassionateWheelData');
      toast.success('All data reset!');
    }
  };

  // Download as PDF
  const downloadPDF = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Please allow popups to download the PDF');
      return;
    }
    
    const currentDate = new Date().toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric'
    });

    const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Compassionate Communication Wheel - Page 25</title>
  <style>
    body { font-family: Georgia, serif; line-height: 1.6; color: #1a1a1a; padding: 40px; max-width: 800px; margin: 0 auto; }
    .header { text-align: center; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 3px solid #2d5a3d; }
    .header h1 { font-size: 28px; color: #2d5a3d; margin-bottom: 8px; }
    .wisdom-box { background: #f0fdf4; border-left: 4px solid #22c55e; padding: 15px 20px; margin: 15px 0; }
    .section { margin: 30px 0; page-break-inside: avoid; }
    .section h2 { font-size: 20px; color: #2d5a3d; border-bottom: 2px solid #2d5a3d; padding-bottom: 8px; }
    .quadrant { background: #f9fafb; border: 2px solid #e5e7eb; border-radius: 8px; padding: 15px; margin: 15px 0; }
    .quadrant.self { border-color: #2d5a3d; }
    .quadrant.other { border-color: #7c3aed; }
    .label { font-weight: bold; color: #555; font-size: 14px; margin-bottom: 5px; }
    .label.needs { color: #22c55e; }
    .label.feelings-met { color: #f59e0b; }
    .label.feelings-unmet { color: #ef4444; }
    .label.strategies { color: #d97706; }
    .value { background: white; border: 1px solid #ddd; border-radius: 4px; padding: 10px; margin-bottom: 10px; min-height: 30px; }
    .value.empty { color: #999; font-style: italic; }
    .footer { margin-top: 40px; text-align: center; font-size: 12px; color: #888; border-top: 1px solid #ddd; padding-top: 20px; }
    @media print { body { padding: 20px; } }
  </style>
</head>
<body>
  <div class="header">
    <h1>🎯 Compassionate Communication Wheel</h1>
    <div>Page 25 • Belonging Sessions E-book</div>
    <div style="margin-top: 10px; color: #666;">${currentDate}</div>
  </div>
  
  <div class="wisdom-box">
    <strong>💡 Remember:</strong> Our conflicts are always between strategies, never between our needs. 
    Drop into the needs — it will get you out of the weeds!
  </div>

  <div class="section">
    <h2>🌍 The Situation</h2>
    <div class="value ${!wheelData.situation ? 'empty' : ''}">${wheelData.situation || '(Not described)'}</div>
  </div>

  <div class="section">
    <h2>💚 My Perspective (Self)</h2>
    <div class="quadrant self">
      <div class="label needs">My Needs (The Green):</div>
      <div class="value ${!wheelData.self.needs.filter(n => n).length ? 'empty' : ''}">${wheelData.self.needs.filter(n => n).join(', ') || '(Not identified)'}</div>
      
      <div class="label feelings-met">Feelings When Needs Met:</div>
      <div class="value ${!wheelData.self.feelingsMet ? 'empty' : ''}">${wheelData.self.feelingsMet || '(Not identified)'}</div>
      
      <div class="label feelings-unmet">Feelings When Needs NOT Met:</div>
      <div class="value ${!wheelData.self.feelingsUnmet ? 'empty' : ''}">${wheelData.self.feelingsUnmet || '(Not identified)'}</div>
      
      <div class="label strategies">Strategies/Requests:</div>
      <div class="value ${!wheelData.self.strategies ? 'empty' : ''}">${wheelData.self.strategies || '(Not identified)'}</div>
    </div>
  </div>

  <div class="section">
    <h2>💜 Their Perspective (Other)</h2>
    <div class="quadrant other">
      <div class="label needs">Their Needs (The Green):</div>
      <div class="value ${!wheelData.other.needs.filter(n => n).length ? 'empty' : ''}">${wheelData.other.needs.filter(n => n).join(', ') || '(Not identified)'}</div>
      
      <div class="label feelings-met">Their Feelings When Needs Met:</div>
      <div class="value ${!wheelData.other.feelingsMet ? 'empty' : ''}">${wheelData.other.feelingsMet || '(Not identified)'}</div>
      
      <div class="label feelings-unmet">Their Feelings When Needs NOT Met:</div>
      <div class="value ${!wheelData.other.feelingsUnmet ? 'empty' : ''}">${wheelData.other.feelingsUnmet || '(Not identified)'}</div>
      
      <div class="label strategies">Their Strategies/Requests:</div>
      <div class="value ${!wheelData.other.strategies ? 'empty' : ''}">${wheelData.other.strategies || '(Not identified)'}</div>
    </div>
  </div>

  <div class="section">
    <h2>✨ Integration & Mutual Strategy</h2>
    <div class="label">Insights Gained:</div>
    <div class="value ${!wheelData.insights ? 'empty' : ''}">${wheelData.insights || '(Not recorded)'}</div>
    
    <div class="label strategies">🏆 Mutual Strategy (The Gold):</div>
    <div class="value ${!wheelData.mutualStrategy ? 'empty' : ''}">${wheelData.mutualStrategy || '(Not identified)'}</div>
  </div>

  <div class="footer">
    <p>Generated from <strong>Belonging Sessions</strong> E-book</p>
    <p>"Drop into the needs, it will get you out of the weeds."</p>
  </div>
</body>
</html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.onload = () => {
      setTimeout(() => printWindow.print(), 250);
    };
    toast.success('PDF download started!');
  };

  // Toggle presentation mode
  const togglePresentationMode = async () => {
    if (!isPresentationMode) {
      try {
        if (document.documentElement.requestFullscreen) {
          await document.documentElement.requestFullscreen();
        }
      } catch (e) { console.log('Fullscreen not available'); }
    } else {
      try {
        if (document.fullscreenElement) {
          await document.exitFullscreen();
        }
      } catch (e) { console.log('Exit fullscreen failed'); }
    }
    setIsPresentationMode(!isPresentationMode);
  };

  // Pulsing Feelings & Needs CTA
  const FeelingsNeedsCTA = ({ className = '' }) => (
    <Button
      onClick={() => setIsHelperOpen(true)}
      className={`relative bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-500 hover:to-pink-500 text-white ${className}`}
      data-testid="feelings-needs-cta"
    >
      <div className="absolute inset-0 rounded-md bg-rose-500/30 animate-ping" style={{ animationDuration: '2s' }} />
      <span className="relative flex items-center gap-2">
        <FeelIcon size={18} />
        Feelings & Needs Tool
        <NeedIcon size={18} />
      </span>
    </Button>
  );

  // Interactive Wheel SVG Component
  const InteractiveWheel = () => (
    <div className="relative w-full max-w-2xl mx-auto aspect-square">
      {/* Background glow */}
      <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/20 via-amber-500/20 to-purple-500/20 rounded-full blur-3xl animate-pulse" />
      
      <svg viewBox="0 0 400 400" className="w-full h-full relative z-10">
        {/* Outer ring - Strategies (Gold) */}
        <circle cx="200" cy="200" r="190" fill="none" stroke="url(#goldGradient)" strokeWidth="30" opacity="0.3" />
        <circle cx="200" cy="200" r="190" fill="none" stroke="url(#goldGradient)" strokeWidth="3" />
        
        {/* Middle ring - Feelings (Red/Amber gradient) */}
        <circle cx="200" cy="200" r="150" fill="none" stroke="url(#feelingsGradient)" strokeWidth="30" opacity="0.3" />
        <circle cx="200" cy="200" r="150" fill="none" stroke="url(#feelingsGradient)" strokeWidth="3" />
        
        {/* Inner ring - Needs (Green) */}
        <circle cx="200" cy="200" r="110" fill="none" stroke="url(#needsGradient)" strokeWidth="30" opacity="0.3" />
        <circle cx="200" cy="200" r="110" fill="none" stroke="url(#needsGradient)" strokeWidth="3" />
        
        {/* Center - Heart/Connection */}
        <circle cx="200" cy="200" r="60" fill="url(#centerGradient)" />
        <text x="200" y="195" textAnchor="middle" fill="white" fontSize="12" fontWeight="bold">Connection</text>
        <text x="200" y="210" textAnchor="middle" fill="white" fontSize="10">& Belonging</text>
        
        {/* Labels */}
        <text x="200" y="25" textAnchor="middle" fill="#D97706" fontSize="11" fontWeight="bold">STRATEGIES / REQUESTS</text>
        <text x="200" y="40" textAnchor="middle" fill="#D97706" fontSize="9">(The Gold)</text>
        
        <text x="200" y="65" textAnchor="middle" fill="#EF4444" fontSize="10" fontWeight="bold">FEELINGS</text>
        <text x="200" y="78" textAnchor="middle" fill="#F59E0B" fontSize="8">When Met ↔ When Unmet</text>
        
        <text x="200" y="105" textAnchor="middle" fill="#22C55E" fontSize="11" fontWeight="bold">NEEDS</text>
        <text x="200" y="118" textAnchor="middle" fill="#22C55E" fontSize="9">(The Green)</text>
        
        {/* Quadrant dividers */}
        <line x1="200" y1="10" x2="200" y2="140" stroke="#64748b" strokeWidth="1" strokeDasharray="4,4" opacity="0.5" />
        <line x1="200" y1="260" x2="200" y2="390" stroke="#64748b" strokeWidth="1" strokeDasharray="4,4" opacity="0.5" />
        <line x1="10" y1="200" x2="140" y2="200" stroke="#64748b" strokeWidth="1" strokeDasharray="4,4" opacity="0.5" />
        <line x1="260" y1="200" x2="390" y2="200" stroke="#64748b" strokeWidth="1" strokeDasharray="4,4" opacity="0.5" />
        
        {/* Quadrant labels */}
        <text x="100" y="100" textAnchor="middle" fill="#22C55E" fontSize="10" fontWeight="bold">MY</text>
        <text x="100" y="112" textAnchor="middle" fill="#22C55E" fontSize="9">NEEDS</text>
        
        <text x="300" y="100" textAnchor="middle" fill="#7C3AED" fontSize="10" fontWeight="bold">THEIR</text>
        <text x="300" y="112" textAnchor="middle" fill="#7C3AED" fontSize="9">NEEDS</text>
        
        <text x="100" y="300" textAnchor="middle" fill="#F59E0B" fontSize="10" fontWeight="bold">MY</text>
        <text x="100" y="312" textAnchor="middle" fill="#F59E0B" fontSize="9">FEELINGS</text>
        
        <text x="300" y="300" textAnchor="middle" fill="#EC4899" fontSize="10" fontWeight="bold">THEIR</text>
        <text x="300" y="312" textAnchor="middle" fill="#EC4899" fontSize="9">FEELINGS</text>
        
        {/* Gradients */}
        <defs>
          <linearGradient id="goldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#F59E0B" />
            <stop offset="100%" stopColor="#D97706" />
          </linearGradient>
          <linearGradient id="feelingsGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#EF4444" />
            <stop offset="50%" stopColor="#F59E0B" />
            <stop offset="100%" stopColor="#22C55E" />
          </linearGradient>
          <linearGradient id="needsGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#22C55E" />
            <stop offset="100%" stopColor="#10B981" />
          </linearGradient>
          <radialGradient id="centerGradient" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#EC4899" />
            <stop offset="100%" stopColor="#8B5CF6" />
          </radialGradient>
        </defs>
      </svg>
      
      {/* Floating sparkles */}
      <div className="absolute top-10 left-10 text-2xl animate-bounce">✨</div>
      <div className="absolute top-10 right-10 text-2xl animate-bounce" style={{ animationDelay: '0.5s' }}>💚</div>
      <div className="absolute bottom-10 left-10 text-2xl animate-bounce" style={{ animationDelay: '1s' }}>❤️</div>
      <div className="absolute bottom-10 right-10 text-2xl animate-bounce" style={{ animationDelay: '1.5s' }}>🏆</div>
    </div>
  );

  const renderContent = () => {
    switch (currentSection) {
      // INTRO
      case 0:
        return (
          <div className="space-y-8 animate-fadeIn" data-testid="wheel-intro">
            <div className="text-center mb-8">
              <div className="w-24 h-24 bg-gradient-to-r from-emerald-500 via-amber-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-2xl shadow-emerald-500/30">
                <GoldIcon size={48} color="white" />
              </div>
              <h2 className="text-4xl font-bold text-white mb-3">Compassionate Communication Wheel</h2>
              <p className="text-xl text-emerald-300">Page 25 • The Heart of NVC</p>
            </div>

            <InteractiveWheel />

            <div className="flex justify-center">
              <FeelingsNeedsCTA className="px-6 py-3 text-lg" />
            </div>

            <div className="bg-gradient-to-br from-emerald-900/40 to-teal-900/30 border border-emerald-500/40 rounded-2xl p-8">
              <p className="text-xl text-emerald-100 leading-relaxed text-center">
                This wheel helps identify <span className="text-emerald-300 font-bold">what needs are alive</span> for a person, 
                <span className="text-rose-300 font-bold"> what feelings occur</span> when needs are met or unmet, and 
                <span className="text-amber-300 font-bold"> what strategies or requests</span> could meet those needs.
              </p>
            </div>

            {/* Key Insight */}
            <div className="bg-gradient-to-r from-purple-900/40 to-pink-900/30 border-2 border-purple-500/50 rounded-2xl p-6 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/20 rounded-full blur-3xl" />
              <div className="relative">
                <div className="flex items-center gap-3 mb-4">
                  <SparkleIcon size={32} color="#A855F7" />
                  <h3 className="text-2xl font-bold text-purple-300">The Golden Insight</h3>
                </div>
                <p className="text-xl text-purple-100 italic">
                  "Drop into the needs, it will get you out of the weeds."
                </p>
              </div>
            </div>

            {/* ElchmE Callout */}
            <ElchmECallout />
          </div>
        );

      // WISDOM PEARLS
      case 1:
        return (
          <div className="space-y-6 animate-fadeIn" data-testid="wheel-wisdom">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-white mb-3">Wisdom Pearls</h2>
              <p className="text-emerald-300">Essential insights for compassionate navigation</p>
            </div>

            <div className="flex justify-center mb-6">
              <FeelingsNeedsCTA />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {wisdomPearls.map((pearl) => {
                const colorClasses = {
                  amber: 'from-amber-900/40 to-orange-900/30 border-amber-500/40',
                  emerald: 'from-emerald-900/40 to-green-900/30 border-emerald-500/40',
                  purple: 'from-purple-900/40 to-indigo-900/30 border-purple-500/40',
                  teal: 'from-teal-900/40 to-cyan-900/30 border-teal-500/40',
                  rose: 'from-rose-900/40 to-red-900/30 border-rose-500/40',
                  pink: 'from-pink-900/40 to-rose-900/30 border-pink-500/40',
                  blue: 'from-blue-900/40 to-indigo-900/30 border-blue-500/40',
                  green: 'from-green-900/40 to-emerald-900/30 border-green-500/40',
                };
                const textColors = {
                  amber: 'text-amber-300',
                  emerald: 'text-emerald-300',
                  purple: 'text-purple-300',
                  teal: 'text-teal-300',
                  rose: 'text-rose-300',
                  pink: 'text-pink-300',
                  blue: 'text-blue-300',
                  green: 'text-green-300',
                };
                
                return (
                  <div
                    key={pearl.id}
                    className={`bg-gradient-to-br ${colorClasses[pearl.color]} border rounded-xl overflow-hidden cursor-pointer transition-all hover:scale-[1.02]`}
                    onClick={() => setExpandedPearl(expandedPearl === pearl.id ? null : pearl.id)}
                  >
                    <div className="p-5">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{pearl.icon}</span>
                          <h3 className={`font-bold ${textColors[pearl.color]}`}>{pearl.title}</h3>
                        </div>
                        {expandedPearl === pearl.id ? (
                          <ArrowDownIcon size={20} color="#94A3B8" style={{ transform: 'rotate(180deg)' }} />
                        ) : (
                          <ArrowDownIcon size={20} color="#94A3B8" />
                        )}
                      </div>
                      {expandedPearl === pearl.id && (
                        <p className="text-slate-200 leading-relaxed animate-in fade-in">
                          {pearl.content}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Big Quote */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6 text-center mt-8">
              <p className="text-2xl text-slate-200 italic">
                "Our conflicts are always between <span className="text-amber-400 font-bold">strategies</span>. 
                They are never between our <span className="text-emerald-400 font-bold">needs</span>."
              </p>
            </div>
          </div>
        );

      // INTERACTIVE WHEEL
      case 2:
        return (
          <div className="space-y-6 animate-fadeIn" data-testid="wheel-interactive">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Map Your Situation</h2>
              <FeelingsNeedsCTA />
            </div>

            {/* Situation */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-5">
              <label className="text-lg font-bold text-slate-200 mb-3 block flex items-center gap-2">
                <ObserveIcon size={20} />
                Describe the Situation or Conflict
              </label>
              <Textarea
                value={wheelData.situation}
                onChange={(e) => setWheelData(prev => ({ ...prev, situation: e.target.value }))}
                placeholder="What's happening? What's the conflict about?"
                className="bg-slate-900/50 border-slate-600 text-white min-h-[80px]"
              />
            </div>

            {/* Two-column layout for Self and Other */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* MY PERSPECTIVE */}
              <div className="bg-gradient-to-br from-emerald-900/30 to-green-900/20 border-2 border-emerald-500/40 rounded-xl p-5">
                <h3 className="text-xl font-bold text-emerald-300 mb-4 flex items-center gap-2">
                  <FeelIcon size={20} />
                  My Perspective
                </h3>
                
                {/* My Needs */}
                <div className="mb-4">
                  <label className="text-sm font-bold text-emerald-400 mb-2 block">🌱 My Needs (The Green)</label>
                  {wheelData.self.needs.map((need, idx) => (
                    <Input
                      key={idx}
                      value={need}
                      onChange={(e) => {
                        const newNeeds = [...wheelData.self.needs];
                        newNeeds[idx] = e.target.value;
                        setWheelData(prev => ({ ...prev, self: { ...prev.self, needs: newNeeds } }));
                      }}
                      placeholder={`Need ${idx + 1}...`}
                      className="bg-emerald-900/20 border-emerald-700/30 text-white mb-2"
                    />
                  ))}
                </div>

                {/* My Feelings When Met */}
                <div className="mb-4">
                  <label className="text-sm font-bold text-amber-400 mb-2 block">😊 Feelings When Needs MET</label>
                  <Textarea
                    value={wheelData.self.feelingsMet}
                    onChange={(e) => setWheelData(prev => ({ ...prev, self: { ...prev.self, feelingsMet: e.target.value } }))}
                    placeholder="Happy, relieved, grateful..."
                    className="bg-amber-900/20 border-amber-700/30 text-white min-h-[60px]"
                  />
                </div>

                {/* My Feelings When Unmet */}
                <div className="mb-4">
                  <label className="text-sm font-bold text-rose-400 mb-2 block">😔 Feelings When Needs NOT Met</label>
                  <Textarea
                    value={wheelData.self.feelingsUnmet}
                    onChange={(e) => setWheelData(prev => ({ ...prev, self: { ...prev.self, feelingsUnmet: e.target.value } }))}
                    placeholder="Frustrated, sad, anxious..."
                    className="bg-rose-900/20 border-rose-700/30 text-white min-h-[60px]"
                  />
                </div>

                {/* My Strategies */}
                <div>
                  <label className="text-sm font-bold text-amber-300 mb-2 block">🎯 My Strategies/Requests</label>
                  <Textarea
                    value={wheelData.self.strategies}
                    onChange={(e) => setWheelData(prev => ({ ...prev, self: { ...prev.self, strategies: e.target.value } }))}
                    placeholder="What I've been doing or wanting..."
                    className="bg-amber-900/20 border-amber-700/30 text-white min-h-[60px]"
                  />
                </div>
              </div>

              {/* THEIR PERSPECTIVE */}
              <div className="bg-gradient-to-br from-purple-900/30 to-indigo-900/20 border-2 border-purple-500/40 rounded-xl p-5">
                <h3 className="text-xl font-bold text-purple-300 mb-4 flex items-center gap-2">
                  <PartnerIcon size={20} />
                  Their Perspective
                </h3>
                
                {/* Their Needs */}
                <div className="mb-4">
                  <label className="text-sm font-bold text-purple-400 mb-2 block">🌱 Their Needs (Guess with Empathy)</label>
                  {wheelData.other.needs.map((need, idx) => (
                    <Input
                      key={idx}
                      value={need}
                      onChange={(e) => {
                        const newNeeds = [...wheelData.other.needs];
                        newNeeds[idx] = e.target.value;
                        setWheelData(prev => ({ ...prev, other: { ...prev.other, needs: newNeeds } }));
                      }}
                      placeholder={`Their need ${idx + 1}...`}
                      className="bg-purple-900/20 border-purple-700/30 text-white mb-2"
                    />
                  ))}
                </div>

                {/* Their Feelings When Met */}
                <div className="mb-4">
                  <label className="text-sm font-bold text-pink-400 mb-2 block">😊 Their Feelings When Needs MET</label>
                  <Textarea
                    value={wheelData.other.feelingsMet}
                    onChange={(e) => setWheelData(prev => ({ ...prev, other: { ...prev.other, feelingsMet: e.target.value } }))}
                    placeholder="What might they feel when their needs are met?"
                    className="bg-pink-900/20 border-pink-700/30 text-white min-h-[60px]"
                  />
                </div>

                {/* Their Feelings When Unmet */}
                <div className="mb-4">
                  <label className="text-sm font-bold text-rose-400 mb-2 block">😔 Their Feelings When Needs NOT Met</label>
                  <Textarea
                    value={wheelData.other.feelingsUnmet}
                    onChange={(e) => setWheelData(prev => ({ ...prev, other: { ...prev.other, feelingsUnmet: e.target.value } }))}
                    placeholder="What might they feel when needs aren't met?"
                    className="bg-rose-900/20 border-rose-700/30 text-white min-h-[60px]"
                  />
                </div>

                {/* Their Strategies */}
                <div>
                  <label className="text-sm font-bold text-indigo-300 mb-2 block">🎯 Their Strategies/Requests</label>
                  <Textarea
                    value={wheelData.other.strategies}
                    onChange={(e) => setWheelData(prev => ({ ...prev, other: { ...prev.other, strategies: e.target.value } }))}
                    placeholder="What have they been doing or wanting?"
                    className="bg-indigo-900/20 border-indigo-700/30 text-white min-h-[60px]"
                  />
                </div>
              </div>
            </div>

            {/* Reminder */}
            <div className="bg-teal-900/30 border border-teal-500/30 rounded-xl p-4 text-center">
              <p className="text-teal-200 italic">
                💡 Remember: Focus on <span className="text-emerald-300 font-bold">needs</span>, not strategies. 
                "Stop making people wrong. Just ask: Is this meeting our needs or not?"
              </p>
            </div>
          </div>
        );

      // INTEGRATION
      case 3:
        return (
          <div className="space-y-8 animate-fadeIn" data-testid="wheel-integration">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-2xl shadow-amber-500/30">
                <GoldIcon size={40} color="#ffffff" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-3">The Gold: Integration</h2>
              <p className="text-amber-300">Discovering Mutual Strategies</p>
            </div>

            <div className="flex justify-center mb-6">
              <FeelingsNeedsCTA />
            </div>

            {/* Insights */}
            <div className="bg-gradient-to-br from-purple-900/30 to-indigo-900/20 border border-purple-500/40 rounded-xl p-6">
              <label className="text-lg font-bold text-purple-300 mb-3 block flex items-center gap-2">
                <InsightIcon size={24} color="#A855F7" />
                What Insights Have Emerged?
              </label>
              <p className="text-slate-400 text-sm mb-3">
                Looking at both perspectives, what do you notice? Where do the needs overlap or complement?
              </p>
              <Textarea
                value={wheelData.insights}
                onChange={(e) => setWheelData(prev => ({ ...prev, insights: e.target.value }))}
                placeholder="I notice that we both need... The conflict seems to be about strategies, not needs... We both want..."
                className="bg-purple-900/20 border-purple-700/30 text-white min-h-[120px]"
              />
            </div>

            {/* Mutual Strategy - THE GOLD */}
            <div className="bg-gradient-to-br from-amber-900/40 to-yellow-900/30 border-2 border-amber-500/50 rounded-2xl p-8 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-40 h-40 bg-amber-500/20 rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-40 h-40 bg-yellow-500/20 rounded-full blur-3xl" />
              
              <div className="relative">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-4xl">🏆</span>
                  <div>
                    <h3 className="text-2xl font-bold text-amber-300">The Gold: Mutual Strategy</h3>
                    <p className="text-amber-200/70 text-sm">A strategy that honors BOTH sets of needs</p>
                  </div>
                </div>
                <Textarea
                  value={wheelData.mutualStrategy}
                  onChange={(e) => setWheelData(prev => ({ ...prev, mutualStrategy: e.target.value }))}
                  placeholder="What strategy could meet both our needs? What request could we both say yes to?"
                  className="bg-amber-900/30 border-amber-500/40 text-white min-h-[120px] text-lg"
                />
              </div>
            </div>

            {/* Final Wisdom */}
            <div className="bg-gradient-to-r from-emerald-900/40 to-teal-900/30 border border-emerald-500/40 rounded-xl p-6">
              <p className="text-xl text-emerald-100 text-center leading-relaxed">
                <span className="text-emerald-300 font-bold">Focus on your needs AND their needs</span> with radical empathy, 
                understanding, and compassion — <span className="text-amber-300 font-bold">solutions will always find us!</span>
              </p>
              <p className="text-emerald-200/70 text-center mt-4 italic">
                This is our superpower. We cannot resist when this occurs. Try it — it's like magic! ✨
              </p>
            </div>

            {/* Completion */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6 text-center">
              <p className="text-slate-300 text-lg mb-4">
                You've completed the <span className="text-amber-400 font-bold">Compassionate Communication Wheel</span>! 🎯
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Button
                  onClick={() => navigate('/workbook/empathetic-alchemy')}
                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white"
                >
                  <SparkleIcon size={16} color="white" className="mr-2" />
                  Try Empathetic Alchemy →
                </Button>
                <Button
                  onClick={() => navigate('/belonging-ebook')}
                  variant="outline"
                  className="border-slate-600 text-slate-300 hover:bg-slate-800"
                >
                  <ChapterIcon size={18} className="mr-2" />
                  Return to eBook
                </Button>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 ${isPresentationMode ? 'overflow-hidden' : ''}`}>
      {/* Header */}
      <header className="sticky top-0 z-50 bg-slate-900/95 backdrop-blur-md border-b border-slate-700/50">
        <div className="max-w-5xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/belonging-ebook')}
                variant="ghost"
                size="sm"
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeftIcon size={16} className="mr-1" />
                Back
              </Button>
              <div className="h-5 w-px bg-slate-700" />
              <div>
                <h1 className="text-lg font-bold text-white">Compassionate Communication Wheel</h1>
                <p className="text-xs text-emerald-400">Page 25 • Core Tool</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button onClick={saveProgress} variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                <SaveIcon size={16} className="mr-1" />
                Save
              </Button>
              <Button onClick={downloadPDF} variant="ghost" size="sm" className="text-slate-400 hover:text-white" title="Download as PDF">
                <DownloadIcon size={16} className="mr-1" />
                PDF
              </Button>
              <Button onClick={resetAll} variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                <ResetIcon size={16} />
              </Button>
              <Button onClick={togglePresentationMode} variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                {isPresentationMode ? <CollapseIcon size={16} /> : <ExpandIcon size={16} />}
              </Button>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-3">
            <Progress value={progress_pct} className="h-1.5 bg-slate-700" />
            <div className="flex justify-between mt-1 text-xs text-slate-500">
              <span>Section {currentSection + 1} of {sections.length}</span>
              <span>Page {sections[currentSection]?.page}</span>
            </div>
          </div>
        </div>
      </header>

      {/* Section Navigation */}
      {!isPresentationMode && (
        <div className="bg-slate-800/50 border-b border-slate-700/30">
          <div className="max-w-5xl mx-auto px-4 py-2">
            <div className="flex gap-2 overflow-x-auto pb-2">
              {sections.map((section, idx) => (
                <button
                  key={section.id}
                  onClick={() => setCurrentSection(idx)}
                  className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition-all ${
                    currentSection === idx
                      ? 'bg-emerald-600 text-white'
                      : 'bg-slate-700/50 text-slate-400 hover:bg-slate-600/50 hover:text-slate-200'
                  }`}
                >
                  {section.title}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className={`max-w-4xl mx-auto px-4 py-8 ${isPresentationMode ? 'h-screen flex items-center' : ''}`}>
        <div className="w-full">
          {renderContent()}
        </div>
      </main>

      {/* Navigation Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-slate-900/95 backdrop-blur-md border-t border-slate-700/50 py-3 px-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Button
            onClick={() => setCurrentSection(prev => Math.max(prev - 1, 0))}
            disabled={currentSection === 0}
            variant="outline"
            className="border-slate-600 text-slate-300 hover:bg-slate-800 disabled:opacity-30"
          >
            <ArrowLeftIcon size={18} className="mr-2" />
            Previous
          </Button>
          
          <div className="text-center">
            <span className="text-slate-400 text-sm">{sections[currentSection]?.title}</span>
          </div>
          
          <Button
            onClick={() => setCurrentSection(prev => Math.min(prev + 1, sections.length - 1))}
            disabled={currentSection === sections.length - 1}
            className="bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-30"
          >
            Next
            <ArrowRightIcon size={18} className="ml-2" />
          </Button>
        </div>
      </div>

      {/* Floating Feelings & Needs Helper */}
      <FloatingFeelingsHelper />

      {/* Feelings & Needs Helper Modal */}
      <FeelingsNeedsHelper 
        isOpen={isHelperOpen} 
        onClose={() => setIsHelperOpen(false)} 
      />
    </div>
  );
}
